import { ScanFace } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MODELO_ROSTRO } from '@/lib/biometria/rostro';

import { resolveAuthErrorMessage } from '../auth.messages';
import { useAuthStore } from '../auth.store';
import { useLoginBiometria } from '../hooks/use-login-biometria';
import type { LoginType } from '../types';
import { AdminLoginForm } from './admin-login-form';
import { CamaraFacialDialog } from './camara-facial-dialog';
import { LoginTabs } from './login-tabs';
import { OperatorLoginForm } from './operator-login-form';

interface LoginCardProps {
  onAuthenticated: () => void;
}

const HEADINGS: Record<LoginType, { title: string; subtitle?: string }> = {
  admin: {
    title: 'Bienvenido de vuelta',
  },
  operator: {
    title: 'Acceso de operador',
    subtitle: 'Registra tu código para ingresar a tu turno.',
  },
};

/**
 * Tarjeta principal del login: encabezado, selector de tipo de acceso y el
 * formulario correspondiente.
 */
export function LoginCard({ onAuthenticated }: LoginCardProps) {
  const [type, setType] = useState<LoginType>('admin');
  const [camaraAbierta, setCamaraAbierta] = useState(false);
  const [biometriaError, setBiometriaError] = useState<string | null>(null);
  /** Cambiar la clave del diálogo reinicia la captura (y la cámara) desde cero. */
  const [intento, setIntento] = useState(0);

  const loginBiometria = useLoginBiometria();

  /**
   * El aviso de "sesión expirada" se captura una sola vez, antes de limpiar el
   * flag del store, para que el mensaje no desaparezca en el siguiente render.
   */
  const [expiredNotice] = useState(() => useAuthStore.getState().sessionExpired);

  useEffect(() => {
    if (expiredNotice) {
      useAuthStore.getState().acknowledgeExpiration();
    }
  }, [expiredNotice]);

  const heading = HEADINGS[type];

  const abrirCamara = () => {
    setBiometriaError(null);
    setCamaraAbierta(true);
  };

  /** Al capturar la cara se intenta entrar; si no coincide, se ofrece la contraseña. */
  const entrarConLaCara = async (descriptores: number[][]) => {
    setBiometriaError(null);

    const descriptor = descriptores[0];

    if (!descriptor) {
      setBiometriaError('No se pudo capturar tu cara. Inténtalo otra vez.');
      return;
    }

    try {
      await loginBiometria.mutateAsync({ descriptor, modelo: MODELO_ROSTRO });
      setCamaraAbierta(false);
      onAuthenticated();
    } catch (error) {
      setBiometriaError(resolveAuthErrorMessage(error));
    }
  };

  return (
    <Card className="overflow-hidden border-ink-100/80 bg-white/95 p-6 backdrop-blur-sm sm:p-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-ink-900">{heading.title}</h1>
        {heading.subtitle ? (
          <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{heading.subtitle}</p>
        ) : null}
      </header>

      {expiredNotice ? (
        <div className="mb-5">
          <Alert variant="warning" title="Tu sesión finalizó">
            Por seguridad cerramos tu sesión. Vuelve a ingresar para continuar.
          </Alert>
        </div>
      ) : null}

      <LoginTabs value={type} onChange={setType} />

      <div
        key={type}
        id={`login-panel-${type}`}
        role="tabpanel"
        aria-labelledby={`login-tab-${type}`}
        className="mt-6 animate-fade-in"
      >
        {type === 'admin' ? (
          <AdminLoginForm onAuthenticated={onAuthenticated} />
        ) : (
          <OperatorLoginForm onAuthenticated={onAuthenticated} />
        )}
      </div>

      <div className="mt-6 border-t border-ink-100 pt-5">
        <Button
          type="button"
          variant="outline"
          size="lg"
          fullWidth
          leftIcon={<ScanFace className="size-5" />}
          onClick={abrirCamara}
          disabled={loginBiometria.isPending}
        >
          Entrar con mi cara
        </Button>
      </div>

      <CamaraFacialDialog
        key={intento}
        open={camaraAbierta}
        onClose={() => setCamaraAbierta(false)}
        titulo="Entrar con la cara"
        descripcion="Mira a la cámara y mantente quieto: la captura se hace sola."
        capturas={1}
        onCapturas={entrarConLaCara}
        ocupado={loginBiometria.isPending}
        error={biometriaError}
        onReintentar={() => {
          setBiometriaError(null);
          setIntento((actual) => actual + 1);
        }}
        textoReintentar="Probar otra vez"
      />
    </Card>
  );
}
